import { List, Map } from 'immutable';
import { format } from 'date-fns';
import { constants as logConstants } from '@saucelabs/sl-component-log';

import * as constants from './../constants';
import { LogLineRecord } from './../records';

const { LEVELS, LEVELS_BY_SEVERITY, POSITIONS } = logConstants;

const initialState = Map({
  active: false,
  bottomHasMore: false,
  filter: Map({
    ignoreCase: true,
    invert: false,
    phrase: '',
    regex: false,
  }),
  displayedLines: List(),
  lines: List(),
  logLevel: LEVELS.WARN,
  paused: false,
  startIndex: 0,
  topHasMore: false,
});

const LOAD_STEP = 10;
const LIMIT = constants.MOBILE_LOG_LINES_LIMIT;

function adjustIndexToBoundaries(desiredStartIndex, size) {
  const bigEnough = desiredStartIndex >= 0;
  const smallEnough = desiredStartIndex <= size - LIMIT;

  if (!bigEnough) {
    return 0;
  }

  if (!smallEnough) {
    // adjust again as it may be too small
    return adjustIndexToBoundaries(size - LIMIT, size);
  }

  return desiredStartIndex;
}

const filterByLogLevel = (lines, logLevel) => {
  const visibleLogLevels = LEVELS_BY_SEVERITY.slice(LEVELS_BY_SEVERITY.indexOf(LEVELS[logLevel]));

  return lines.filter((line) => visibleLogLevels.includes(line.level));
};

const filterByPhrase = (lines, filter) => {
  if (!filter.get('phrase')) return lines;

  let matchFunction;

  if (filter.get('regex')) {
    const regex = new RegExp(filter.get('phrase'), filter.get('ignoreCase') ? 'i' : '');
    matchFunction = (line) => line.search(regex) >= 0;
  } else if (filter.get('ignoreCase')) {
    const phrase = filter.get('phrase').toLowerCase();
    matchFunction = (line) => line.toLowerCase().includes(phrase);
  } else {
    matchFunction = (line) => line.includes(filter.get('phrase'));
  }

  return lines.filter((line) => matchFunction(line.message) !== filter.get('invert'));
};

const getDisplayedLines = (lines, logLevel, filters) => (
  filterByPhrase(
    filterByLogLevel(lines, logLevel),
    filters,
  )
);

const mobileLogReducer = (state = initialState, { type, payload }) => {
  const prevLines = state.get('lines');

  switch (type) {
    case constants.MOBILE_LOG_SHOW: {
      return state.set('active', payload);
    }
    case constants.MOBILE_DEV_TOOLS_SHOW:
    case constants.MOBILE_VITALS_SHOW: {
      if (payload) {
        return state.set('active', false);
      }
      return state;
    }

    // Message format:
    // "2019-09-02 14:27:41.131\tDEBUG:\tBattCtrl : onReceive - ACTION_BATTERY_CHANGED : foo = 86"
    case constants.MOBILE_LOG_LINE_ADD: {
      const newLineValues = payload.split('\t');
      const dateValueInISO8601 = newLineValues[0].replace(' ', 'T'); // Required for "new Date()" to work in Safari
      const newLine = new LogLineRecord({
        key: prevLines.size + 1,
        level: newLineValues[1].replace(':', ''),
        // Some of the messages contain tabs themselves. In that case the first 2 tabs are removed
        message: newLineValues.length === 3 ? newLineValues[2] : payload.replace(/^.*?\t.*?\t/, ''),
        time: format(new Date(dateValueInISO8601), constants.MOBILE_LOG_TIME_FORMAT),
        timestamp: dateValueInISO8601,
      });

      const allLines = prevLines.push(newLine);
      return state.set('lines', allLines);
    }
    case constants.MOBILE_LOG_UPDATE_DISPLAYED_LINES: {
      const displayedLines = getDisplayedLines(prevLines, state.get('logLevel'), state.get('filter'));
      const startIndex = state.get('paused') ?
        state.get('startIndex') :
        adjustIndexToBoundaries(displayedLines.size - LIMIT, displayedLines.size);

      return state.withMutations((mutableState) => (
        mutableState
          .set('startIndex', startIndex)
          .set('topHasMore', startIndex > 0)
          .set('bottomHasMore', displayedLines.size > startIndex + LIMIT)
          .set('displayedLines', displayedLines.slice(startIndex, startIndex + LIMIT))
      ));
    }
    case constants.MOBILE_LOG_LINES_LOAD_MORE: {
      let startIndex = state.get('startIndex');
      let paused = true;

      if (payload === POSITIONS.TOP) {
        startIndex = adjustIndexToBoundaries(startIndex - LOAD_STEP, prevLines.size);
      }

      if (payload === POSITIONS.BOTTOM) {
        startIndex = adjustIndexToBoundaries(startIndex + LOAD_STEP, prevLines.size);
        paused = startIndex < prevLines.size - LIMIT;
      }

      return state.set('startIndex', startIndex).set('paused', paused);
    }
    case constants.MOBILE_LOG_LINES_RESUME: {
      const startIndex = adjustIndexToBoundaries(prevLines.size - LIMIT, prevLines.size);

      return state.set('startIndex', startIndex).set('paused', false);
    }
    case constants.MOBILE_LOG_LINES_PAUSE:
      return state.set('paused', true);
    case constants.MOBILE_LOG_SET_LOG_LEVEL:
      return state.set('logLevel', payload).set('startIndex', 0);
    case constants.MOBILE_LOG_CLEAR:
      return state.withMutations((mutableState) => (
        mutableState
          .set('bottomHasMore', initialState.get('bottomHasMore'))
          .set('displayedLines', initialState.get('displayedLines'))
          .set('lines', initialState.get('lines'))
          .set('startIndex', initialState.get('startIndex'))
          .set('topHasMore', initialState.get('topHasMore'))
      ));
    case constants.MOBILE_LOG_UPDATE_FILTERS: {
      const allLines = state.get('lines');
      const filter = state.get('filter').merge(payload);
      const displayedLines = getDisplayedLines(allLines, state.get('logLevel'), filter);

      const startIndex = state.get('paused') ?
        state.get('startIndex') :
        adjustIndexToBoundaries(displayedLines.size - LIMIT, displayedLines.size);

      return state.withMutations((mutableState) => (
        mutableState
          .set('filter', filter)
          .set('startIndex', startIndex)
          .set('topHasMore', startIndex > 0)
          .set('bottomHasMore', displayedLines.size > startIndex + LIMIT)
          .set('displayedLines', displayedLines.slice(startIndex, startIndex + LIMIT))
      ));
    }
    case constants.MOBILE_SESSION_STOP.SUCCESS:
      return initialState;
    default:
      return state;
  }
};

export default mobileLogReducer;
export { initialState };
